// EmailSettingsView — admin configura Resend (API key, From) e gerencia
// domínios verificados.
//
// Seções:
//   1. Credenciais — API key (masked) + From + Domínio default
//   2. Domínios — lista do Resend com status + DNS records a adicionar no
//      provedor DNS. Botões: Adicionar, Verificar, Remover.
//
// A API key NUNCA volta em claro do server — só preview "re_XXXX••••YYYY".
// Pra trocar, o admin digita uma nova; pra remover, envia string vazia.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Globe,
  Key,
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Trash2
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type EmailConfigRow,
  type EmailRuntimeStatus
} from "./api";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface ResendDomain {
  id: string;
  name: string;
  status: string; // "not_started" | "pending" | "verified" | "failed" | "temporary_failure"
  region?: string;
  created_at?: string;
  records?: Array<{
    record: string;
    name: string;
    type: string;
    ttl?: string;
    status?: string;
    value: string;
    priority?: number;
  }>;
}

const phase = signal<"loading" | "ready" | "error">("loading");
const config = signal<EmailConfigRow | null>(null);
const runtime = signal<EmailRuntimeStatus | null>(null);
const domains = signal<ResendDomain[]>([]);
const errMsg = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const cfgRes = await api.getEmailConfig();
    config.value = cfgRes.config;
    runtime.value = cfgRes.runtime;
    // Lista domínios — falha aqui não bloqueia a tela; só mostra config
    try {
      const domRes = await api.listEmailDomains();
      if (domRes.status === 200 && domRes.result) {
        const result = domRes.result as { data?: ResendDomain[] };
        domains.value = result.data ?? [];
      } else {
        domains.value = [];
      }
    } catch {
      domains.value = [];
    }
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function EmailSettingsView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-3">
        <Skeleton class="h-32 w-full" />
        <Skeleton class="h-64 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }
  if (!config.value || !runtime.value) return null;

  return (
    <div class="space-y-6">
      <CredentialsSection />
      <DomainsSection />
    </div>
  );
}

function CredentialsSection() {
  const cfg = config.value!;
  const rt = runtime.value!;
  const [newKey, setNewKey] = useState("");
  const [from, setFrom] = useState(cfg.resendFrom);
  const [defaultDomain, setDefaultDomain] = useState(cfg.resendDefaultDomain);
  const [saving, setSaving] = useState(false);

  const dirty =
    newKey.trim().length > 0 ||
    from !== cfg.resendFrom ||
    defaultDomain !== cfg.resendDefaultDomain;

  async function save() {
    setSaving(true);
    try {
      const patch: Record<string, string> = {};
      if (newKey.trim()) patch.resendApiKey = newKey.trim();
      if (from !== cfg.resendFrom) patch.resendFrom = from.trim();
      if (defaultDomain !== cfg.resendDefaultDomain) {
        patch.resendDefaultDomain = defaultDomain.trim();
      }
      await api.updateEmailConfig(patch);
      setNewKey("");
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao salvar",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  async function clearKey() {
    const ok = await openConfirm({
      title: "Remover API Key salva?",
      message:
        "Vamos zerar a chave do DB. Se houver chave no .env do servidor, ela continua valendo como fallback. Pra parar de enviar emails, remova de ambos.",
      confirmLabel: "Remover do DB",
      danger: true
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.updateEmailConfig({ resendApiKey: "" });
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao remover",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    const to = await openPrompt({
      title: "Enviar email de teste",
      message:
        "Pra qual email queremos mandar um teste? Vamos usar a config atual (key + From).",
      placeholder: "seu@email.com",
      confirmLabel: "Enviar"
    });
    if (!to) return;
    void openAlert({
      title: "Teste",
      message:
        "Endpoint de envio-teste ainda não publicado. Por enquanto, valide via 'Esqueci minha senha' no /auth/."
    });
  }

  return (
    <section class="space-y-4 rounded-md border border-border bg-card p-5">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Key className="size-4 text-violet-600" />
            Credenciais do servidor de email
          </h2>
          <p class="mt-1 text-xs text-muted-foreground">
            Configure aqui o Resend sem mexer no .env. Útil pra trocar a key
            em manutenção ou em produção.
          </p>
        </div>
        <RuntimeBadge runtime={rt} />
      </header>

      {/* API Key */}
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          API Key do Resend
        </label>
        <div class="flex items-center gap-2">
          <div class="flex-1 rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs">
            {cfg.resendApiKeySet ? (
              <span>
                <CheckCircle2 className="mr-1 inline size-3.5 text-emerald-600" />
                {cfg.resendApiKeyMasked}{" "}
                <span class="ml-2 text-muted-foreground">(armazenada no DB)</span>
              </span>
            ) : rt.envKeyConfigured ? (
              <span class="text-muted-foreground">
                <ShieldCheck className="mr-1 inline size-3.5 text-blue-600" />
                Usando RESEND_API_KEY do .env (fallback)
              </span>
            ) : (
              <span class="text-muted-foreground">
                <AlertCircle className="mr-1 inline size-3.5 text-amber-600" />
                Nenhuma chave configurada — emails NÃO serão enviados
              </span>
            )}
          </div>
          {cfg.resendApiKeySet && (
            <button
              type="button"
              onClick={clearKey}
              disabled={saving}
              class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              <Trash2 className="size-3" />
              Remover
            </button>
          )}
        </div>
        <div class="mt-2">
          <input
            type="password"
            value={newKey}
            onInput={(e) => setNewKey((e.target as HTMLInputElement).value)}
            placeholder="re_••••••••••••••••••••••••••  (cole pra trocar)"
            title="Nova API Key do Resend"
            class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          />
          <p class="mt-1 text-[10px] text-muted-foreground">
            Crie em resend.com/api-keys. A key vai ficar criptografada no DB
            do Postgres — só admins veem o preview "re_XXXX••••YYYY".
          </p>
        </div>
      </div>

      {/* From */}
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Remetente padrão (From)
        </label>
        <input
          type="text"
          value={from}
          onInput={(e) => setFrom((e.target as HTMLInputElement).value)}
          placeholder="Simulador <no-reply@seudominio.com>"
          title="Remetente padrão"
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <p class="mt-1 text-[10px] text-muted-foreground">
          Use{" "}
          <code class="rounded bg-muted px-1">onboarding@resend.dev</code> pra
          testar sem domínio verificado (limites baixos). Pra produção,
          verifique seu domínio na seção abaixo e use{" "}
          <code class="rounded bg-muted px-1">no-reply@seudominio.com</code>.
          {rt.effectiveFrom && from !== rt.effectiveFrom && (
            <>
              {" "}
              <span class="text-amber-700 dark:text-amber-400">
                Efetivo agora: <code>{rt.effectiveFrom}</code>
              </span>
            </>
          )}
        </p>
      </div>

      {/* Default domain */}
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Domínio principal (para referência)
        </label>
        <input
          type="text"
          value={defaultDomain}
          onInput={(e) => setDefaultDomain((e.target as HTMLInputElement).value)}
          placeholder="seudominio.com"
          title="Domínio principal"
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>

      <div class="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={sendTest}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent"
        >
          <Send className="size-3" />
          Enviar email de teste
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Salvar
        </button>
      </div>
    </section>
  );
}

function RuntimeBadge({ runtime }: { runtime: EmailRuntimeStatus }) {
  if (!runtime.configured) {
    return (
      <span class="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
        <AlertCircle className="size-3" />
        Sem chave
      </span>
    );
  }
  return (
    <span class="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="size-3" />
      Ativo via {runtime.source.toUpperCase()}
    </span>
  );
}

function DomainsSection() {
  const list = domains.value;
  const cfg = config.value!;
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function addDomain() {
    const name = await openPrompt({
      title: "Adicionar domínio",
      message:
        "Digite o domínio que vai enviar emails (ex: simulador.com). Você vai precisar criar 3 records DNS no provedor — vamos te mostrar quais.",
      placeholder: "seudominio.com",
      confirmLabel: "Adicionar no Resend"
    });
    if (!name) return;
    setAdding(true);
    try {
      const r = await api.addEmailDomain(name);
      if (r.status >= 400) {
        const err = r.result as { message?: string; name?: string };
        throw new Error(err?.message ?? "Falha ao adicionar domínio.");
      }
      await load();
      void openAlert({
        title: "Domínio adicionado",
        message:
          "Agora copie os records DNS abaixo e adicione no seu provedor (Cloudflare/Route53/Registro.br). Depois clique em 'Verificar'."
      });
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setAdding(false);
    }
  }

  async function verifyDomain(id: string) {
    setBusyId(id);
    try {
      await api.verifyEmailDomain(id);
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setBusyId(null);
    }
  }

  async function deleteDomain(id: string, name: string) {
    const ok = await openConfirm({
      title: "Remover domínio?",
      message: `Vamos remover ${name} do Resend. Você precisará adicionar de novo se quiser usar.`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await api.deleteEmailDomain(id);
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e)
      });
    } finally {
      setBusyId(null);
    }
  }

  if (!cfg.resendApiKeySet && !runtime.value?.envKeyConfigured) {
    return (
      <section class="rounded-md border border-amber-500/30 bg-amber-500/5 p-5 text-sm">
        <Globe className="mr-1 inline size-4 text-amber-700" />
        Configure a API Key acima pra gerenciar domínios.
      </section>
    );
  }

  return (
    <section class="space-y-3 rounded-md border border-border bg-card p-5">
      <header class="flex items-start justify-between gap-2">
        <div>
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Globe className="size-4 text-violet-600" />
            Domínios verificados
          </h2>
          <p class="mt-1 text-xs text-muted-foreground">
            Liga o Resend ao seu domínio pra parar de usar{" "}
            <code class="rounded bg-muted px-1">onboarding@resend.dev</code>.
            Você precisa ter acesso ao DNS do domínio.
          </p>
        </div>
        <button
          type="button"
          onClick={addDomain}
          disabled={adding}
          class="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {adding ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          Adicionar domínio
        </button>
      </header>

      {list.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum domínio cadastrado no Resend ainda.
        </div>
      ) : (
        <div class="space-y-3">
          {list.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              busy={busyId === d.id}
              onVerify={() => verifyDomain(d.id)}
              onDelete={() => deleteDomain(d.id, d.name)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function DomainCard({
  domain,
  busy,
  onVerify,
  onDelete
}: {
  domain: ResendDomain;
  busy: boolean;
  onVerify: () => void;
  onDelete: () => void;
}) {
  const statusStyle = useMemo(() => {
    switch (domain.status) {
      case "verified":
        return {
          bg: "bg-emerald-500/10",
          text: "text-emerald-700 dark:text-emerald-300",
          icon: <CheckCircle2 className="size-3" />,
          label: "Verificado"
        };
      case "pending":
      case "not_started":
        return {
          bg: "bg-amber-500/10",
          text: "text-amber-700 dark:text-amber-300",
          icon: <Loader2 className="size-3" />,
          label: "Aguardando DNS"
        };
      default:
        return {
          bg: "bg-destructive/10",
          text: "text-destructive",
          icon: <AlertCircle className="size-3" />,
          label: domain.status
        };
    }
  }, [domain.status]);

  return (
    <div class="rounded-md border border-border bg-background p-4">
      <header class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <Mail className="size-4 text-muted-foreground" />
            <code class="font-semibold">{domain.name}</code>
            <span
              class={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}
            >
              {statusStyle.icon}
              {statusStyle.label}
            </span>
          </div>
          <div class="mt-0.5 text-[10px] text-muted-foreground">
            ID: <code>{domain.id}</code> · Região: {domain.region ?? "—"}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={onVerify}
            disabled={busy || domain.status === "verified"}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            Verificar
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            <Trash2 className="size-3" />
            Remover
          </button>
        </div>
      </header>

      {domain.records && domain.records.length > 0 && (
        <div class="mt-3">
          <div class="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Records DNS a criar no provedor
          </div>
          <div class="overflow-x-auto">
            <table class="w-full text-[11px]">
              <thead class="bg-muted/40 text-muted-foreground">
                <tr>
                  <th class="px-2 py-1 text-left">Tipo</th>
                  <th class="px-2 py-1 text-left">Nome</th>
                  <th class="px-2 py-1 text-left">Valor</th>
                  <th class="px-2 py-1 text-left">TTL</th>
                  <th class="px-2 py-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {domain.records.map((r, i) => (
                  <tr key={i} class="border-t border-border align-top">
                    <td class="px-2 py-1.5 font-mono font-semibold">{r.type}</td>
                    <td class="px-2 py-1.5 font-mono break-all">{r.name}</td>
                    <td class="px-2 py-1.5 font-mono break-all">{r.value}</td>
                    <td class="px-2 py-1.5">{r.ttl ?? "Auto"}</td>
                    <td class="px-2 py-1.5">
                      {r.status === "verified" ? (
                        <CheckCircle2 className="size-3 text-emerald-600" />
                      ) : (
                        <Loader2 className="size-3 text-amber-600" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p class="mt-2 text-[10px] text-muted-foreground">
            Cole esses registros no DNS do seu domínio (Cloudflare, Route53,
            Registro.br…). Quando todos ficarem como Verificado, clique
            "Verificar" pra atualizar o status.
          </p>
        </div>
      )}
    </div>
  );
}
